import {gql} from '@apollo/client';
import {range} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SampleConstants from 'constants/SampleConstants';
import {isInaccessibleStorable} from 'constants/SampleConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {
  getSampleStatusTooltip,
  doesCurrentUserHaveAccessForItem,
  isItemAvailable,
  doesItemHaveBlockingStatusWarning,
} from 'inventory/sample-status/SampleStatusPresentationUtils';
import LoadingFullScreen from 'nav/full-screen/LoadingFullScreen';
import notifications from 'notifications';
import {GridCellProps as ReduceSamplesUIGridCellProps} from 'samples/reduceSamplesUI';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import GridCellKey from 'test/lib/cypress/shared/GridCellKey';
import {
  GREEN_DARKER,
  GREEN_LIGHTER,
  ORANGE_DARKER,
  ORANGE_LIGHTER,
  RED_DARKER,
  RED_LIGHTEST,
  WHITE,
} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import APIUtil from 'util/APIUtil';
import * as keyboard from 'util/Keyboard';
import pick from 'util/pick';
import {ElementMouseEvent} from 'util/Types';

import * as SampleUtil from '../SampleUtil';
import {
  GridCellsExpirationInfoQuery,
  GridCellsExpirationInfoQuery_storable_BoxNode_PlateNode as BoxOrPlateNode,
  GridCellsExpirationInfoQueryVariables,
} from './__generated/GridCellsExpirationInfoQuery';

const NUM_LETTERS = 26;

interface GridCellProps {
  cell: SampleConstants.MinimalContainerGridCell | null;
  className?: string;
  innerClassName?: string;
  showFilled?: boolean;
  showIndex?: boolean;
  rowIndex: number;
  columnIndex: number;
  tooltipTitle: string | null;
  width: number;
  isSelected?: boolean;
  onClickCell?: (({rowIndex, columnIndex}: {rowIndex: number; columnIndex: number}) => void) | null;
  onShiftClickCell?: (({rowIndex, columnIndex}: {rowIndex: number; columnIndex: number}) => void) | null;
  dataTestAttrs: DataTestAttrs;
}

/**
 * A cell-aware grid cell component
 */
export function GridCell(props: GridCellProps): JSX.Element {
  const handleClickCell = (e: ElementMouseEvent): void => {
    const cell = props.cell || {rowIndex: props.rowIndex, columnIndex: props.columnIndex};
    if (keyboard.isShiftClick(e) && props.onShiftClickCell) {
      props.onShiftClickCell(cell);
    } else if (props.onClickCell) {
      props.onClickCell(cell);
    }
  };

  const {
    cell,
    className,
    innerClassName,
    showFilled,
    showIndex,
    tooltipTitle,
    isSelected,
    rowIndex,
    columnIndex,
    width,
    dataTestAttrs,
  } = props;
  const isFilled = cell && !isInaccessibleStorable(cell.container) && cell.container.contents.length > 0;
  const isExpired =
    (cell != null &&
      BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE &&
      !isInaccessibleStorable(cell.container) &&
      cell.container.expirationInfo?.isTrackingEnabled &&
      cell.container.expirationInfo?.isExpired) ??
    false;
  const isUnavailable =
    cell != null && !isInaccessibleStorable(cell.container) && !isItemAvailable(cell.container);
  const isUnauthorized =
    cell != null &&
    hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1) &&
    !isInaccessibleStorable(cell.container) &&
    !doesCurrentUserHaveAccessForItem(cell.container);
  const sampleStatusTooltip =
    cell != null && isInaccessibleStorable(cell.container)
      ? 'Inaccessible'
      : (isUnavailable || isUnauthorized || isExpired) && !isInaccessibleStorable(cell!.container)
        ? getSampleStatusTooltip(cell!.container, {
            includeAvailability: isUnavailable,
            includeAuthorization: isUnauthorized,
            includeExpiration: isExpired,
            hideAssignee: false,
          })
        : '';

  const tooltip = tooltipTitle || sampleStatusTooltip;
  const isEmpty = Boolean(!cell);
  const hasStatusWarning = isUnavailable || isUnauthorized || isExpired;
  const hasBlockingStatusWarning =
    cell != null &&
    (isInaccessibleStorable(cell.container) || doesItemHaveBlockingStatusWarning(cell.container));
  const shouldShowIndex = showIndex && width <= 13;

  let contentsClass;
  let dataTestAttrsKey;

  // Because aphrodite defaults to !important styling, we need to make innerClassName and contentsClass
  // mutually exclusive for innerClassName to take precedence as was originally expected. This is necessary to
  // support GridCells when used for the Set from Matrix Scanner flow.
  if (!innerClassName) {
    if (hasBlockingStatusWarning) {
      contentsClass = css(styles.gridCellContainerStatusBlockingWarning);
      dataTestAttrsKey = GridCellKey.BLOCKING_STATUS_WARNING;
    } else if (hasStatusWarning) {
      contentsClass = css(styles.gridCellContainerStatusWarning);
      dataTestAttrsKey = GridCellKey.NON_BLOCKING_STATUS_WARNING;
    } else if (!showFilled || (!isEmpty && !isFilled)) {
      contentsClass = css(styles.gridCellContainerNoContents);
      dataTestAttrsKey = GridCellKey.NO_CONTENTS;
    } else if (showFilled && !isEmpty) {
      contentsClass = css(styles.gridCellContainerWithContents);
      dataTestAttrsKey = GridCellKey.WITH_CONTENTS;
    }
  }

  if (dataTestAttrsKey == null && isEmpty) {
    dataTestAttrsKey = GridCellKey.EMPTY;
  }

  return (
    <div
      className={classNames('gridCell', className, {
        'gridCell--selected': isSelected,
        'u-pointer': Boolean(props.onClickCell),
      })}
      onClick={handleClickCell}
      {...getTestProps({
        // If `dataTestAttrs` already has `key`, that value wins
        // TODO(BNCH-23399): Replace `key` override pattern
        key: isFilled ? GridCellKey.WITH_CONTENTS : isEmpty ? GridCellKey.EMPTY : GridCellKey.NO_CONTENTS,
        ...dataTestAttrs,
      })}
    >
      <Tooltip isDisabled={!tooltip} text={tooltip}>
        <div
          className={classNames(innerClassName, 'gridCell-inner', contentsClass, {
            'gridCell-inner--empty': isEmpty,
          })}
          {...getTestProps(
            dataTestAttrsKey
              ? {component: 'BaseGridCell', element: 'innerGridCell', key: dataTestAttrsKey}
              : {component: 'BaseGridCell', element: 'innerGridCell'}
          )}
        >
          {shouldShowIndex && SampleUtil.formatGridNumber(rowIndex, columnIndex, width)}
        </div>
      </Tooltip>
    </div>
  );
}

GridCell.defaultProps = {tooltipTitle: null, showFilled: true};

export const GRID_CELLS_EXPIRATION_INFO_QUERY = gql`
  query GridCellsExpirationInfoQuery($gridId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    storable(id: $gridId) {
      ... on StorableInterface {
        id
      }
      ... on GridInterface {
        cells {
          container {
            ... on ContainerNode {
              id
              expirationInfo {
                id
                expirationDate
                isExpired
                isTrackingEnabled
              }
            }
            ...InaccessibleInfoGraphQL
          }
          columnIndex
          rowIndex
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface GridCellsProps {
  className?: string;
  grid: SampleConstants.GridWithMinimalCells;
  /** Props to pass through for every grid cell. If only tooltips for a few positions are desired,
   * `gridCellTooltipTextByPosition` is a lighter weight alternative. If both props are provided with
   * defined tooltips, the tooltip from `gridCellProps` takes precedence.
   */
  gridCellProps?: Array<Array<ReduceSamplesUIGridCellProps>> | null;
  isCompact: boolean;
  onClickCell?: (({rowIndex, columnIndex}: {rowIndex: number; columnIndex: number}) => void) | null;
  onShiftClickCell?: (({rowIndex, columnIndex}: {rowIndex: number; columnIndex: number}) => void) | null;
  selectedCells?: Array<{columnIndex: number; rowIndex: number}>;
  showFilledCells: boolean;
  /** Optional tooltips for the grid cells at the included positions, indexed with grid position strings
   * provided by SampleUtil.formatGridCoordinates (e.g. A1).
   * If the styling & tooltips of every cell need to be overridden by the parent, providing the full
   * `gridCellProps` is likely a better fit. If both are provided with defined tooltips, the tooltip from
   * `gridCellProps` takes precedence.
   */
  gridCellTooltipTextByPosition?: {[gridPosition: string]: string};
}

GridCells.defaultProps = {
  isCompact: true,
  showFilledCells: true,
};

export function GridCells(props: GridCellsProps): JSX.Element | null {
  const {
    grid,
    gridCellProps,
    gridCellTooltipTextByPosition,
    selectedCells,
    onClickCell,
    onShiftClickCell,
    showFilledCells,
  } = props;
  const showIndicesInCells = APIUtil.apiIdToType(grid.id) === ModelTypeConstants.BOX;
  const rowCoordinateCellClassNames = classNames('gridCell-header', {
    [css(styles.expandedHeaderGridCell)]: grid.cells.length > NUM_LETTERS,
  });
  const expirationInfoQueryResult = useBenchlingQuery<
    GridCellsExpirationInfoQuery,
    GridCellsExpirationInfoQueryVariables
  >(GRID_CELLS_EXPIRATION_INFO_QUERY, {
    variables: {gridId: grid.id},
    // TODO(BNCH-98600): Remove this fetch policy once the cache updates correctly after container updates
    fetchPolicy: 'cache-and-network',
    renderCachedDataWhenRefetching: true,
  });
  if (expirationInfoQueryResult.type === QueryResultType.PENDING) {
    return <LoadingFullScreen />;
  } else if (expirationInfoQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load grid; try reloading the page.');
    return null;
  }
  let gridCellsWithExpirationInfo = grid.cells;
  const expirationInfoGrid = expirationInfoQueryResult.data.storable;
  if (!isInaccessibleStorable(expirationInfoGrid) && SampleUtil.isGrid(grid.id)) {
    gridCellsWithExpirationInfo = grid.cells.map((row, rowIndex) =>
      row.map((gridCell, columnIndex) => {
        const expirationInfoCells = (expirationInfoGrid as BoxOrPlateNode).cells;
        if (
          gridCell != null &&
          !isInaccessibleStorable(gridCell.container) &&
          rowIndex < expirationInfoCells.length &&
          columnIndex < expirationInfoCells[rowIndex].length
        ) {
          const expirationInfoCell = expirationInfoCells[rowIndex][columnIndex];
          if (expirationInfoCell && !isInaccessibleStorable(expirationInfoCell.container)) {
            const expirationInfo = expirationInfoCell.container.expirationInfo;
            return {
              ...gridCell,
              container: {
                ...gridCell.container,
                expirationInfo: pick(expirationInfo, 'expirationDate', 'isExpired', 'isTrackingEnabled'),
              },
            };
          }
          return gridCell;
        }
        return gridCell;
      })
    );
  }
  return (
    <div
      className={classNames('gridCells-container u-no-select', props.className, {
        'gridCells-container--compact': props.isCompact,
      })}
      {...getTestProps({component: 'GridCells', element: 'root'})}
    >
      <div className="gridCells-row">
        <div className={rowCoordinateCellClassNames} />
        {range(0, grid.storableSchema.width - 1).map((c) => (
          <div className="gridCell-header" key={c}>
            <div className="gridCell-header-inner">{SampleUtil.formatGridColumnCoordinate(c)}</div>
          </div>
        ))}
      </div>
      {gridCellsWithExpirationInfo.map((rowCells, rowIndex) => (
        <div
          className="gridCells-row"
          key={rowIndex}
          {...getTestProps({component: 'GridCells', element: 'row'})}
        >
          <div className={rowCoordinateCellClassNames}>
            <div className="gridCell-header-inner">{SampleUtil.formatGridRowCoordinate(rowIndex)}</div>
          </div>
          {rowCells.map((cell, columnIndex) => (
            <GridCell
              cell={cell}
              showFilled={showFilledCells}
              showIndex={showIndicesInCells}
              rowIndex={rowIndex}
              columnIndex={columnIndex}
              width={grid.cells[0].length}
              isSelected={
                selectedCells &&
                selectedCells.some(
                  (selectedCell) =>
                    selectedCell.rowIndex === rowIndex && selectedCell.columnIndex === columnIndex
                )
              }
              key={columnIndex}
              onClickCell={onClickCell}
              onShiftClickCell={onShiftClickCell}
              {...{
                tooltipTitle:
                  gridCellTooltipTextByPosition?.[SampleUtil.formatGridCoordinates(rowIndex, columnIndex)] ??
                  null,
                ...(gridCellProps?.[rowIndex]?.[columnIndex] ?? {}),
                dataTestAttrs: {
                  ...(gridCellProps?.[rowIndex]?.[columnIndex]?.dataTestAttrs ?? {}),
                  component: 'GridCells',
                  element: 'cell',
                },
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export const styles = StyleSheet.create({
  expandedHeaderGridCell: {
    minWidth: '22px',
  },
  gridCellContainerStatusWarning: {
    background: ORANGE_LIGHTER,
    color: ORANGE_DARKER,
  },
  gridCellContainerStatusBlockingWarning: {
    background: RED_LIGHTEST,
    color: RED_DARKER,
  },
  gridCellContainerNoContents: {
    backgroundColor: WHITE,
  },
  gridCellContainerWithContents: {
    backgroundColor: GREEN_LIGHTER,
    color: GREEN_DARKER,
  },
});

// Prevents rerender when `selectedCells` on the parent component is modified via immutable helpers, but
// the value does not change.
export default React.memo(GridCells, isEqual);
